import type { HostActions } from '@makemydeal/dr-activities-recent-deals';
import { getDealListUrl } from '@makemydeal/dr-dash-store/selectors/config';
import { currentOfferHasUnsavedChanges } from '@makemydeal/dr-dash-store/selectors/unsavedChangesSelectors';
import { setPendingNavigation } from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import { getCommonOrgId } from '@makemydeal/dr-dash-store/selectors/offerRedux';
import {
    COMMON_ORG_ID_PARAM,
    ENABLE_TOGGLES,
    PR_NUMBER_SESSION_STORAGE_NAME,
    PR_ENV_SESSION_STORAGE_NAME,
    offerSessionUtils
} from '@makemydeal/dr-shared-ui-utils';

/**
 * Build the deal URL for opening a deal by dealId.
 * Uses the current origin and constructs the Manager View deal URL.
 */
const buildDealUrl = (dealId: string): string => {
    const origin = window.location.origin;
    return `${origin}?dealXgId=${encodeURIComponent(dealId)}`;
};

/**
 * Build the Manager View URL for creating a quote from an organization.
 * With `OrgID` set and no `dealXgId`, the app bootstraps via the `initOrg`
 * route (opens "as from an organization").
 */
const buildCreateQuoteUrl = (coOrgId: string): string => {
    // Toggle passed via query param to force Quote Mode on when opening the Manager View tab (by default it is opened via org, but need to pass it via dealer)
    // TODO: clean it up when the Quote Mode feature is fully released and no longer behind a toggle
    const QUOTE_MODE_TOGGLE = 'FEAT278181_MV_Quick_Quote_Mode';
    const params = new URLSearchParams();
    params.set(COMMON_ORG_ID_PARAM, coOrgId);

    // Carry over PR environment params from sessionStorage so the PR CloudFront distribution routes correctly
    const prNumber = offerSessionUtils.getPrNumber();
    const prEnv = offerSessionUtils.getPrEnvironmentName();
    if (prNumber) params.set(PR_NUMBER_SESSION_STORAGE_NAME, prNumber);
    if (prEnv) params.set(PR_ENV_SESSION_STORAGE_NAME, prEnv);

    // Merge enableToggles from sessionStorage with the required Quote Mode toggle
    const existingToggles = offerSessionUtils.getSessionValue(ENABLE_TOGGLES);
    const toggleSet = new Set(existingToggles ? existingToggles.split(',') : []);
    toggleSet.add(QUOTE_MODE_TOGGLE);
    params.set(ENABLE_TOGGLES, [...toggleSet].join(','));

    return `${window.location.origin}?${params.toString()}`;
};

export const hostActions: HostActions = {
    openDeal: (dealId: string, target?: string) => {
        return (dispatch: any, getState: any) => {
            const state = getState();
            const dealUrl = buildDealUrl(dealId);

            // If opening in a new tab, proceed directly without checking unsaved changes
            if (target === '_blank') {
                window.open(dealUrl, '_blank');
                return;
            }

            // Check for unsaved changes before navigating in the same window
            const hasUnsavedChanges = currentOfferHasUnsavedChanges(state);

            if (hasUnsavedChanges) {
                // Store pending navigation to be executed after handling unsaved changes
                dispatch(setPendingNavigation(dealUrl));
            } else {
                // No unsaved changes, navigate directly
                window.location.assign(dealUrl);
            }
        };
    },
    redirectToDealList: () => {
        return (_: any, getState: any) => {
            const state = getState();
            const configUrl = getDealListUrl(state);
            window.open(configUrl, '_blank');
        };
    },
    createQuote: (coOrgId?: string) => {
        return (_: any, getState: any) => {
            const orgId = coOrgId || getCommonOrgId(getState());
            if (!orgId) return;
            window.open(buildCreateQuoteUrl(orgId), '_blank', 'noopener');
        };
    }
};
